import { Platform } from 'react-native';

export type ImageLibraryOptions = any;
export type Asset = any;
export type ImagePickerResponse = {
  didCancel?: boolean;
  errorCode?: string;
  assets?: Array<{ uri?: string; type?: string; fileName?: string }>;
};

// Web-compatible file picker using HTML input element
function createFileInput(options?: ImageLibraryOptions): Promise<ImagePickerResponse> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      resolve({ didCancel: true });
      return;
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    // Make it invisible but still functional
    input.style.position = 'fixed';
    input.style.top = '0';
    input.style.left = '0';
    input.style.width = '1px';
    input.style.height = '1px';
    input.style.opacity = '0';
    input.style.overflow = 'hidden';
    input.style.zIndex = '-1';
    // Don't use pointerEvents: 'none' as it can prevent clicks on some browsers
    
    // Determine media type
    const mediaType = options?.mediaType || 'photo';
    if (mediaType === 'photo') {
      input.accept = 'image/*';
    } else if (mediaType === 'video') {
      input.accept = 'video/*';
    } else {
      // 'mixed' or default - allow both
      input.accept = 'image/*,video/*';
    }
    
    // Handle multiple selection
    if (options?.allowsMultipleSelection || (options?.selectionLimit && options.selectionLimit > 1)) {
      input.multiple = true;
    }
    
    let resolved = false;
    let focusTimeout: ReturnType<typeof setTimeout> | null = null;
    
    const cleanup = () => {
      if (focusTimeout) {
        clearTimeout(focusTimeout);
        focusTimeout = null;
      }
      if (input.parentNode) {
        try {
          input.parentNode.removeChild(input);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
    
    input.onchange = (event: Event) => {
      if (resolved) return;
      const target = event.target as HTMLInputElement;
      const files = target.files;
      
      // Clear any pending focus timeout
      if (focusTimeout) {
        clearTimeout(focusTimeout);
        focusTimeout = null;
      }
      
      if (!files || files.length === 0) {
        resolved = true;
        resolve({ didCancel: true });
        cleanup();
        return;
      }
      
      const assets: Array<{ uri?: string; type?: string; fileName?: string }> = [];
      let processedCount = 0;
      const totalFiles = files.length;
      
      if (totalFiles === 0) {
        resolved = true;
        resolve({ didCancel: true });
        cleanup();
        return;
      }
      
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          if (resolved) return;
          const result = e.target?.result as string;
          assets.push({
            uri: result,
            type: file.type || (file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image/jpeg' : 'video/mp4'),
            fileName: file.name,
            // Store file size for validation (web only)
            fileSize: file.size,
          });
          
          processedCount++;
          if (processedCount === totalFiles) {
            resolved = true;
            resolve({
              didCancel: false,
              assets,
            });
            cleanup();
          }
        };
        
        reader.onerror = () => {
          if (resolved) return;
          processedCount++;
          if (processedCount === totalFiles) {
            resolved = true;
            resolve({
              didCancel: false,
              errorCode: 'READ_ERROR',
              assets: assets.length > 0 ? assets : undefined,
            });
            cleanup();
          }
        };
        
        try {
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error reading file:', error);
          processedCount++;
          if (processedCount === totalFiles) {
            resolved = true;
            resolve({
              didCancel: false,
              errorCode: 'READ_ERROR',
              assets: assets.length > 0 ? assets : undefined,
            });
            cleanup();
          }
        }
      });
    };
    
    // Handle cancellation - detect when window regains focus without file selection
    const handleFocus = () => {
      // Wait a bit to see if onchange fires
      focusTimeout = setTimeout(() => {
        if (!resolved) {
          // Check if input still has no files after a delay
          if (!input.files || input.files.length === 0) {
            resolved = true;
            resolve({ didCancel: true });
            cleanup();
          }
        }
      }, 500); // Increased delay to give onchange time to fire
    };
    
    // Add input to DOM first - must be synchronous for user gesture context
    document.body.appendChild(input);
    
    // Set up focus listener before triggering click
    window.addEventListener('focus', handleFocus, { once: true });
    
    // Detect iOS for special handling
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Trigger click immediately - must be in same call stack as user gesture
    // On mobile browsers (iOS Safari, Chrome Android), file input clicks must
    // happen synchronously within the user gesture event handler
    try {
      if (isIOS) {
        // For iOS, we need to call click() synchronously to maintain user gesture context
        // Make input slightly visible and ensure it's ready
        input.style.position = 'fixed';
        input.style.top = '0';
        input.style.left = '0';
        input.style.width = '1px';
        input.style.height = '1px';
        input.style.opacity = '0.01'; // Slightly visible for iOS
        input.style.zIndex = '9999';
        input.style.pointerEvents = 'auto';
        
        // Force a reflow to ensure the input is ready
        void input.offsetHeight;
        
        // Try to click immediately (synchronously)
        try {
          input.click();
        } catch (syncError) {
          // If synchronous click fails, try requestAnimationFrame as fallback
          console.warn('Synchronous click failed, trying async:', syncError);
          requestAnimationFrame(() => {
            try {
              input.click();
            } catch (error) {
              console.error('Error triggering file input click on iOS:', error);
              window.removeEventListener('focus', handleFocus);
              resolved = true;
              resolve({ didCancel: true, errorCode: 'CLICK_ERROR' });
              cleanup();
            }
          });
        }
      } else {
        // For other platforms, use a microtask to ensure DOM is ready
        Promise.resolve().then(() => {
          try {
            input.click();
          } catch (error) {
            console.error('Error triggering file input click:', error);
            window.removeEventListener('focus', handleFocus);
            resolved = true;
            resolve({ didCancel: true, errorCode: 'CLICK_ERROR' });
            cleanup();
          }
        });
      }
    } catch (error) {
      console.error('Error setting up file input:', error);
      window.removeEventListener('focus', handleFocus);
      resolved = true;
      resolve({ didCancel: true, errorCode: 'SETUP_ERROR' });
      cleanup();
    }
  });
}

export async function launchImageLibrary(options?: ImageLibraryOptions): Promise<ImagePickerResponse> {
  if (Platform.OS === 'web') {
    return createFileInput(options);
  }
  // For non-web platforms, return cancelled (should use native implementation)
  return { didCancel: true };
}

export async function launchCamera(_opts?: any): Promise<ImagePickerResponse> {
  if (Platform.OS === 'web') {
    // On web, we can use getUserMedia for camera, but for simplicity, 
    // we'll use file input with capture attribute
    return new Promise((resolve) => {
      if (typeof document === 'undefined') {
        resolve({ didCancel: true });
        return;
      }
      
      const input = document.createElement('input');
      input.type = 'file';
      input.style.display = 'none';
      input.accept = 'image/*,video/*';
      input.capture = 'environment'; // Use back camera if available
      
      input.onchange = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;
        
        if (!files || files.length === 0) {
          resolve({ didCancel: true });
          document.body.removeChild(input);
          return;
        }
        
        const file = files[0];
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const result = e.target?.result as string;
          resolve({
            didCancel: false,
            assets: [{
              uri: result,
              type: file.type,
              fileName: file.name,
              // Store file size for validation (web only)
              fileSize: file.size,
            }],
          });
          document.body.removeChild(input);
        };
        
        reader.onerror = () => {
          resolve({
            didCancel: false,
            errorCode: 'READ_ERROR',
          });
          document.body.removeChild(input);
        };
        
        reader.readAsDataURL(file);
      };
      
      input.oncancel = () => {
        resolve({ didCancel: true });
        document.body.removeChild(input);
      };
      
      document.body.appendChild(input);
      input.click();
    });
  }
  return { didCancel: true };
}


